/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import type { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { getSourceDescription } from '@rover/rsdk/src/modules/Network/userAgent';
import { AnalyticsEvent } from '@rover/types/src/Analytics';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import emitMetric from '@rover/utilities/emitMetric';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';

import { CarouselScrollSource } from '../../../components/lists/Carousel/utils/onPaginateEvent.types';
import { PageSectionId } from '../SeoLocation/hooks/useSectionRef';

export enum SitterCardType {
  DEFAULT = 'default',
  CAROUSEL = 'carousel',
}

export enum SearchResultLayoutType {
  DEFAULT = 'default',
  CAROUSEL = 'carousel',
}

export enum ValuePropFilterOptionType {
  NO_CAT = 'no-cat',
  NO_DOG = 'no-dog',
  HAS_FENCED_YARD = 'has-fenced-yard',
  NOT_FIXED = 'not-fixed',
  ONLY_ONE_PET = 'only-one-pet',
  ACCEPTS_PUPPIES = 'accepts-puppies',
}

export type InputEventParameters = {
  /**
   * _Service type slug for the page that the event was fired on._
   *
   * @deprecated Legacy English `SeoSlugEnum` value — does not localize. Prefer
   * `internalServiceTypeSlug` for new events.
   */
  serviceTypeSlug?: ServiceTypeSlugEnum;
  /**
   * _Backend-derived localized service-type slug for the page that the event was fired on._
   */
  internalServiceTypeSlug?: string;
};

/**
 * Base class for Seo specific AnalyticsEvents
 */
export abstract class SeoServiceTypeAnalyticsEvent extends AnalyticsEvent {
  abstract eventName: string;

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.marketing.growth;

  constructor(eventProperties: InputEventParameters) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user updates the service type search filter
 *
 * &nbsp;
 */
export class UpdateServiceType extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-search-form-update-service-type';

  constructor(
    eventProperties: {
      /**
       * _Service type that the user selected from the dropdown._
       */
      selectedServiceType: SearchFilters['serviceType'];
    } & InputEventParameters
  ) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user updates the location search filter
 *
 * &nbsp;
 */
export class UpdateLocation extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-search-form-update-location';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user enters start/end dates into the
 * date inputs for a given section.
 *
 * &nbsp;
 */
export class SelectDates extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-search-form-select-dates';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user clicks on a search result before being sent to /search/.
 *
 * &nbsp;
 */
export class ClickSitterCard extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-search-results-click-sitter-card';

  constructor(eventProperties: { cardType: SitterCardType } & InputEventParameters) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user clicks the cta / search button in the Landing Search Form.
 *
 * &nbsp;
 */
export class SearchFormCtaClick extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-search-form-cta-click';
}

export const emitSearchFormCtaAnalytics = (eventProperties: InputEventParameters): void => {
  const metricName = 'landing-search-form-cta-click';
  const tags = {
    ...eventProperties,
    platform: getSourceDescription(),
  };
  storeCrossPageAnalyticsEvent(new SearchFormCtaClick({ ...eventProperties }));
  emitMetric(metricName, tags);
};

type CarouselPaginateActionEventParameters = {
  pageStart: number;
  pageEnd: number;
  scrollSource: CarouselScrollSource;
};

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO location pages e.g. /seattle--wa--dog-boarding/
 *
 * Fires when a user paginates (either by clicking the buttons or swiping) on the search results carousel.
 *
 * &nbsp;
 */
export class CarouselPaginateAction extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-search-carousel-paginate';

  constructor(eventProperties: CarouselPaginateActionEventParameters & InputEventParameters) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO location pages e.g. /seattle--wa--dog-boarding/
 *
 * Fires when a user paginates (either by clicking the buttons or swiping) on the reviews carousel.
 *
 * &nbsp;
 */
export class ReviewsCarouselPaginateAction extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-reviews-carousel-paginate';

  constructor(eventProperties: CarouselPaginateActionEventParameters & InputEventParameters) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user clicks the cta below the search results to see more.
 *
 * &nbsp;
 */
export class SearchResultsCtaClick extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-search-results-cta-click';

  constructor(eventProperties: { layoutType: SearchResultLayoutType } & InputEventParameters) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user clicks the cta below the reviews carousel to see more.
 *
 * &nbsp;
 */
export class ReviewsCtaClick extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-reviews-cta-click';

  constructor(eventProperties: InputEventParameters) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user clicks one of the value proposition tiles to a filtered search.
 *
 * &nbsp;
 */
export class ValuePropFilterOptionsCtaClick extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-value-prop-cta-click';

  constructor(
    eventProperties: { valuePropFilterOption: ValuePropFilterOptionType } & InputEventParameters
  ) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a user clicks one of the pill buttons to scroll to a specific section on the page.
 *
 * &nbsp;
 */
export class ScrollToSectionClick extends SeoServiceTypeAnalyticsEvent {
  eventName = 'seo-location-page-scroll-to-section-click';

  constructor(eventProperties: { sectionId: PageSectionId } & InputEventParameters) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a specific section on the page is scrolled into view.
 *
 * &nbsp;
 */
export class SectionViewed extends SeoServiceTypeAnalyticsEvent {
  eventName = 'seo-location-page-section-viewed';

  constructor(eventProperties: { sectionId: PageSectionId } & InputEventParameters) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a search result card is at least 75% visible on the screen.
 *
 * &nbsp;
 */
export class ViewSitterCard extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-search-results-sitter-card-viewed';

  constructor(
    eventProperties: {
      cardType: SitterCardType;
      sitterResultPersonOpk: string;
    } & InputEventParameters
  ) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires when a review card is at least 75% visible on the screen.
 *
 * &nbsp;
 */
export class ViewReviewCard extends SeoServiceTypeAnalyticsEvent {
  eventName = 'landing-review-card-viewed';

  constructor(eventProperties: { index: number } & InputEventParameters) {
    super(eventProperties);
  }
}

export enum FooterAccordionSection {
  NEARBY_LOCATIONS = 'nearby-locations',
  OTHER_SERVICES = 'other-services',
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on SEO pages e.g. /seattle--wa--dog-boarding/, /cat-sitting/
 *
 * Fires the first time a user toggles one of the footer link accordion items.
 *
 * &nbsp;
 */
export class FooterLinksAccordionToggle extends SeoServiceTypeAnalyticsEvent {
  eventName = 'seo-location-page-footer-links-accordion-toggle';

  constructor(
    eventProperties: { accordionSection: FooterAccordionSection } & Required<
      Pick<InputEventParameters, 'internalServiceTypeSlug'>
    >
  ) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a user clicks the "Sign up" button in the Not Ready to Book banner.
 *
 * &nbsp;
 */
export class NotReadyToBookSignUpClick extends AnalyticsEvent {
  eventName = 'not-ready-to-book-sign-up-click';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.marketing.growth;
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a user clicks the "Find a sitter" button in the Service Benefits section.
 *
 * &nbsp;
 */
export class ServiceBenefitsFindSitterClick extends AnalyticsEvent {
  eventName = 'service-benefits-find-sitter-click';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.marketing.growth;
}
